import { OneStemBrand } from '@/components/brand/OneStemBrand'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAppForm } from '@/lib/form'
import { authQueries, useLoginMutation } from '@/lib/services/auth/auth.query'
import { loginSchema } from '@/lib/services/auth/auth.schema'
import { logoutActiveStripeConnectInstance } from '@/lib/stripe-connect-session'
import { getErrorMessage, isSafeRelativePath } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Eye, EyeOff } from 'lucide-react'
import { useEffectEvent } from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'

export const Route = createFileRoute('/(auth)/login')({
  // Accept an optional redirect search param to redirect to after login
  validateSearch: z.object({
    next: z.string().optional().catch(''), // where to redirect
    reason: z.string().optional().catch(''), // whether to show a session expired message
  }),
  loader: () => ({ isReadOnly: false }),
  component: Login,
})

export async function runLoginPageLoad({
  href,
  ensureAuthStatus,
  clearQueryClient,
  logoutStripeConnect,
  navigateToAuthenticatedDestination,
  showSessionExpired,
  replaceUrl,
}) {
  try {
    const url = new URL(href)
    const next = url.searchParams.get('next') || ''
    const didExpireSession = url.searchParams.get('reason') === 'session-expired'

    if (didExpireSession) {
      url.searchParams.delete('reason')
      replaceUrl(url)
    }

    let status = null
    try {
      status = await ensureAuthStatus()
    } catch {
      status = null
    }

    if (status?.is_authenticated && status?.organization_id) {
      return await navigateToAuthenticatedDestination({
        organizationId: status.organization_id,
        next,
      })
    }

    if (status?.is_authenticated === false) {
      await logoutStripeConnect()
    }

    if (didExpireSession) {
      setTimeout(() => showSessionExpired())
    }

    clearQueryClient()
  } catch {
    // Ignore login-page convenience errors. If this fails, the form still works.
  }
}

export function Login() {
  const loginMutation = useLoginMutation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const path = usePathHelper()
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  // We're basically going to clear the query cache every time someone lands on the login page.
  // Additionally, we will display any "flash" message (if we got redirected here due to session expiration).
  // This is also a good time to check if the user arriving here is already logged in.
  const onLoginPageLoad = useEffectEvent(async () => {
    await runLoginPageLoad({
      href: window.location.href,
      ensureAuthStatus: () =>
        queryClient.fetchQuery({
          ...authQueries.status(),
          staleTime: 0,
        }),
      clearQueryClient: () => queryClient.clear(),
      logoutStripeConnect: logoutActiveStripeConnectInstance,
      navigateToAuthenticatedDestination: ({ organizationId, next }) => {
        if (isSafeRelativePath(next)) {
          return navigate({ to: next, replace: true })
        }

        return navigate({
          ...path.admin.index().using({ orgId: organizationId }).link,
          replace: true,
        })
      },
      showSessionExpired: () => toast.error('Your session has expired. Please log in again.'),
      replaceUrl: (url) => window.history.replaceState({}, '', url),
    })
  })

  useEffect(() => {
    void onLoginPageLoad()
  }, [])

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
      remember: true,
    },
    // This is here because Laravel always returns a failed login as a "username" error
    // That makes a user *have* to change their username to re-submit the form without this
    canSubmitWhenInvalid: true,
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await loginMutation.mutateAsync(value)
        } catch (error) {
          toast.error('Login failed', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const homepageUrl = import.meta.env.VITE_APP_URL?.replace('admin.', '')

  return (
    <>
      <div className="absolute right-2 top-2">
        <ThemeToggle />
      </div>
      <div className="flex flex-col gap-y-4 h-screen items-center justify-center px-4 sm:px-0">
        <a href={homepageUrl}>
          <OneStemBrand className="h-20" />
        </a>
        <Card className="w-full sm:max-w-md">
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>
              Enter your email and password to login to your account
            </CardDescription>
          </CardHeader>
          <CardContent className="mb-4">
            <form.AppForm>
              <form.Form>
                <div className="grid w-full items-center gap-6">
                  <form.FormError />
                  <form.AppField name="email" validators={{ onChange: loginSchema.shape.email }}>
                    {(field) => <field.EmailInput label="Email" />}
                  </form.AppField>
                  <form.AppField
                    name="password"
                    validators={{ onChange: loginSchema.shape.password }}
                  >
                    {(field) => (
                      <field.TextInput
                        id="password"
                        type={isPasswordVisible ? 'text' : 'password'}
                        label="Password"
                        autoComplete="current-password"
                        inputEnd={
                          <ToggleButton
                            type="button"
                            pressed={isPasswordVisible}
                            variant="ghost"
                            pressedVariant="ghost"
                            size="icon"
                            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                            className="size-7 text-muted-foreground hover:text-foreground"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => setIsPasswordVisible((current) => !current)}
                          >
                            {isPasswordVisible ? (
                              <EyeOff className="size-4" aria-hidden="true" />
                            ) : (
                              <Eye className="size-4" aria-hidden="true" />
                            )}
                          </ToggleButton>
                        }
                        labelEnd={
                          <Link to="/forgot-password" tabIndex={-1} className="text-sm ml-auto">
                            <Typography variant="label" interaction="hoverUnderline">
                              Forgot your password?
                            </Typography>
                          </Link>
                        }
                      />
                    )}
                  </form.AppField>
                  <form.AppField
                    name="remember"
                    validators={{ onChange: loginSchema.shape.remember }}
                  >
                    {(field) => <field.CheckboxInput id="remember" label="Remember me" />}
                  </form.AppField>
                  <div>
                    <form.SubmitButton mutation={loginMutation}>Login</form.SubmitButton>
                  </div>
                </div>
              </form.Form>
            </form.AppForm>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
